import cx from "classnames"
import { graphql } from "gatsby"
import PropTypes from "prop-types"
import { useState } from "react"
import Button from "components/Button"
import ContactSalesModal from "components/ContactSalesModal"
import { H2, P2 } from "components/Typography"
import { richTextToHtml } from "utils/richTextToHtml"
import * as css from "./PricingSection.module.css"
import TickIcon from "./tick.inline.svg"
import Section from "components/Layout/Section"
import SectionHeader from "components/Layout/SectionHeader"

function PricingSection(props) {
  const { title, description, headingAlignment, pricings } = props
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)
  const [contactSource, setContactSource] = useState("")
  return (
    <Section className={css.root}>
      <SectionHeader
        className={cx(css.header)}
        level="h1"
        title={title}
        subtitle={description}
        centered={headingAlignment === "Align Center"}
        margin
        marginTop
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {pricings?.map(
          (
            {
              title: priceName,
              icon,
              description: priceDetails,
              price,
              priceUnit,
              priceText,
              badge,
              ctaText,
              ctaLink,
              ctaTrackingName,
              ctaContactModal,
              ctaContactModalSource,
              highlight,
              customOptionsHeading,
              customOptions,
              featureListHeading,
              features,
              addOnListHeading,
              addOnList,
              aiHighlight,
            },
            i
          ) => (
            <div key={i}>
              <div className={cx(css.pricingWrapper, highlight && css.active)}>
                {badge && <span className={css.badge}>{badge}</span>}
                {icon && (
                  <img
                    className={css.tierIcon}
                    src={icon}
                    alt=""
                    aria-hidden="true"
                  />
                )}
                <div className={css.titleContainer}>
                  <H2 className={css.title}>{priceName}</H2>
                  <P2 className={css.description}>{priceDetails}</P2>
                </div>
                <div className={css.pricingContainer}>
                  <div className={css.priceLine}>
                    <h1 className={css.price}>{price}</h1>
                    {priceUnit && (
                      <span className={css.priceUnit}>{priceUnit}</span>
                    )}
                  </div>
                  <div
                    className={css.description}
                    dangerouslySetInnerHTML={{
                      __html: richTextToHtml(JSON.parse(priceText.raw)),
                    }}
                  />
                </div>
                <Button
                  href={ctaLink}
                  onClick={
                    ctaContactModal
                      ? (e) => {
                          e.preventDefault()
                          setContactSource(
                            ctaContactModalSource || `Pricing - ${priceName}`
                          )
                          setIsContactModalOpen(true)
                        }
                      : undefined
                  }
                  className={cx(css.button, highlight && css.activeButton)}
                  trackClick={ctaTrackingName}
                >
                  {ctaText}
                </Button>
                <hr className="my-4 border-gray-200" />
                <P2 className={css.bold}>{featureListHeading}</P2>
                {features.map(({ content }, j) => (
                  <ListItem key={j}>{content}</ListItem>
                ))}
                {aiHighlight && (
                  <div className={css.aiHighlight}>
                    <P2 className={css.aiHighlightHeading}>
                      {aiHighlight.heading}
                      <span className={css.aiHighlightSparkle} aria-hidden>
                        ✨
                      </span>
                    </P2>
                    {aiHighlight.items.map(({ content }, j) => (
                      <AIHighlightItem key={j}>{content}</AIHighlightItem>
                    ))}
                  </div>
                )}
                {customOptions && customOptions.length > 0 && (
                  <div className="mt-10">
                    <P2 className={css.bold}>{customOptionsHeading}</P2>
                    <div className="flex flex-wrap gap-x-2 gap-y-2">
                      {customOptions.map(({ content }, j) => (
                        <CustomOption key={j}>{content}</CustomOption>
                      ))}
                    </div>
                  </div>
                )}
                {addOnList && addOnList.length > 0 && (
                  <div className="mt-10">
                    <P2 className={css.bold}>{addOnListHeading}</P2>
                    {addOnList.map(({ content }, j) => (
                      <ListItem key={j}>{content}</ListItem>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        )}
      </div>
      <ContactSalesModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        sourcePage={contactSource}
      />
    </Section>
  )
}

function ListItem({ children, ...props }) {
  return (
    <div className={css.tick} {...props}>
      <div className={css.iconWrap}>
        <TickIcon />
      </div>
      <div
        dangerouslySetInnerHTML={{
          __html: children,
        }}
      />
    </div>
  )
}

function CustomOption({ children, ...props }) {
  return (
    <span className={css.tag} {...props}>
      {children}
    </span>
  )
}

function BotIcon() {
  return (
    <svg
      className={css.aiHighlightIcon}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="4"
        y="7"
        width="16"
        height="12"
        rx="3"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <circle cx="9" cy="13" r="1.3" fill="currentColor" />
      <circle cx="15" cy="13" r="1.3" fill="currentColor" />
      <path
        d="M12 4v3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="12" cy="3.5" r="1.1" fill="currentColor" />
    </svg>
  )
}

function AIHighlightItem({ children }) {
  return (
    <div className={css.aiHighlightItem}>
      <BotIcon />
      <div
        dangerouslySetInnerHTML={{
          __html: children,
        }}
      />
    </div>
  )
}

export default PricingSection

PricingSection.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  headingAlignment: PropTypes.oneOf(["Align Left", "Align Center"]),
  pricings: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      icon: PropTypes.string,
      description: PropTypes.string.isRequired,
      price: PropTypes.string.isRequired,
      priceUnit: PropTypes.string,
      priceDescription: PropTypes.string,
      badge: PropTypes.string,
      ctaText: PropTypes.string.isRequired,
      ctaLink: PropTypes.string.isRequired,
      ctaTrackingName: PropTypes.string.isRequired,
      highlight: PropTypes.bool.isRequired,
      features: PropTypes.arrayOf(
        PropTypes.shape({
          content: PropTypes.string.isRequired,
        })
      ).isRequired,
      featureListHeading: PropTypes.string.isRequired,
      aiHighlight: PropTypes.shape({
        heading: PropTypes.string.isRequired,
        items: PropTypes.arrayOf(
          PropTypes.shape({
            content: PropTypes.string.isRequired,
          })
        ).isRequired,
      }),
    })
  ).isRequired,
}

AIHighlightItem.propTypes = {
  children: PropTypes.string.isRequired,
}

export const query = graphql`
  fragment PricingSection on ContentfulPricingSection {
    id
    __typename
    title
    description
    headingAlignment
    pricings {
      title
      description
      price
      priceText {
        raw
      }
      ctaText
      ctaLink
      ctaTrackingName
      highlight
      customOptionsHeading
      customOptions {
        content
      }
      featureListHeading
      features {
        content
      }
      addOnListHeading
      addOnList {
        content
      }
    }
  }
`
